import { useMemo, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '@/lib/formatRelativeTime';
import { AlertCircle, ArrowLeft, RefreshCw, Send, Trash2 } from 'lucide-react';
import {
  Button,
  cn,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger
} from '@bliks-io/ui';
import type { ProcessDraft } from '@bliks-io/contracts';
import { SaveStatusIndicator } from '@/pages/processes/recording/components/SaveStatusIndicator';
import { DRAFT_ISSUES_SIDEBAR_ID } from './DraftIssuesSidebar';

interface Props {
  draft: ProcessDraft;
  isSaving: boolean;
  saveFailed: boolean;
  isPublishing: boolean;
  isDiscarding: boolean;
  issueCount: number;
  issuesOpen: boolean;
  onToggleIssues: () => void;
  onShowIssues: () => void;
  issuesToggleRef?: Ref<HTMLButtonElement>;
  onBack: () => void;
  onPublish: () => void;
  onDiscard: () => void;
  onPublishAndReRequestAll: () => void;
}

export function DraftEditorToolbar({
  draft,
  isSaving,
  saveFailed,
  isPublishing,
  isDiscarding,
  issueCount,
  issuesOpen,
  onToggleIssues,
  onShowIssues,
  issuesToggleRef,
  onBack,
  onPublish,
  onDiscard,
  onPublishAndReRequestAll
}: Props) {
  const { t, i18n } = useTranslation();
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const updatedAtRelative = useMemo(() => {
    const updated = draft.updatedAt instanceof Date ? draft.updatedAt : new Date(draft.updatedAt);
    return formatRelativeTime(updated, i18n.language);
  }, [draft.updatedAt, i18n.language]);

  const publishDisabled = isPublishing || isDiscarding;
  // aria-disabled, not disabled: a disabled button swallows the click that shows the issues.
  const publishBlocked = issueCount > 0;

  return (
    <div className="flex items-center gap-2 p-2 bg-white border-b border-line shadow-sm">
      <button
        onClick={onBack}
        disabled={isDiscarding}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-fg-secondary hover:text-foreground hover:bg-muted rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-fg-secondary"
      >
        <ArrowLeft size={16} />
        {t('common.back')}
      </button>

      <div className="flex items-center gap-2 text-xs text-muted-foreground pl-3 shrink-0">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-warning-surface text-warning border border-warning-line whitespace-nowrap shrink-0">
          {t('processDraft.draftBadge')}
        </span>
        <span>{t('processDraft.lastSaved', { when: updatedAtRelative })}</span>
      </div>

      <div className="flex-1" />

      <SaveStatusIndicator isSaving={isSaving} saveFailed={saveFailed} />

      {issueCount > 0 && (
        <button
          ref={issuesToggleRef}
          type="button"
          onClick={onToggleIssues}
          aria-expanded={issuesOpen}
          aria-controls={issuesOpen ? DRAFT_ISSUES_SIDEBAR_ID : undefined}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-colors',
            issuesOpen
              ? 'border-destructive bg-destructive text-destructive-foreground'
              : 'border-destructive/40 bg-destructive/5 text-destructive hover:bg-destructive/10'
          )}
        >
          <AlertCircle className="size-3.5" />
          {t('processDraft.issues.count', { count: issueCount })}
        </button>
      )}

      {confirmingDiscard ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-fg-secondary">{t('processDraft.discardConfirm')}</span>
          <Button
            size="small"
            variant="outline"
            disabled={isDiscarding}
            onClick={() => setConfirmingDiscard(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button size="small" variant="destructive" loading={isDiscarding} onClick={onDiscard}>
            {t('processDraft.discard')}
          </Button>
        </div>
      ) : (
        <Button size="small" variant="outline" onClick={() => setConfirmingDiscard(true)}>
          <Trash2 className="w-3.5 h-3.5 mr-1" />
          {t('processDraft.discard')}
        </Button>
      )}

      <ContextMenu>
        <ContextMenuTrigger asChild>
          <Button
            size="small"
            onClick={publishBlocked ? onShowIssues : onPublish}
            loading={isPublishing}
            disabled={publishDisabled}
            aria-disabled={publishBlocked || undefined}
          >
            <Send className="w-3.5 h-3.5 mr-1" />
            {t('processDraft.publish')}
          </Button>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            onSelect={onPublishAndReRequestAll}
            disabled={publishDisabled || publishBlocked}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-2" />
            {t('processDraft.surveys.publishReRequestAll')}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </div>
  );
}
